/**
 * The plan as a decision: goal, items with an op glyph, the irreversible ones marked, the first
 * few then "Show all N". Rendered INSIDE the approval card so the reader approves what they see.
 *
 * The full revision — assumptions, open questions, coverage, org findings, excluded components —
 * is `PlanRevisionView` in the workspace Task section, one click away ("View full plan").
 */
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { EntityChip } from "@/components/molecules/multi-agent/EntityChip";
import { buildChipIndex, chipTargetFor } from "@/services/multi-agent/laneV2/entityChips";
import { PlanItemDetail } from "./PlanItemDetail";
import { HELD_BACK_LABEL, isHeldBack } from "./views";
import type { PackageView as PackageViewData, PlanView, TaskSnapshot } from "@/services/multi-agent/laneV2/types";

const ITEMS_SHOWN = 8;
/** Open questions shown on the card itself; the rest are counted (a long list belongs in the full plan). */
const QUESTIONS_SHOWN = 3;

export const OP_GLYPH: Record<string, { glyph: string; className: string }> = {
  create: { glyph: "+", className: "text-[var(--ma-state-ok-text)]" },
  modify: { glyph: "±", className: "text-[var(--ma-state-warn-text)]" },
  delete: { glyph: "−", className: "text-[var(--ma-state-err-text)]" },
};

export function LaneV2PlanSummary({
  plan,
  snapshot,
  onOpenItem,
  onOpenFull,
  hideQuestions = false,
}: {
  plan: PlanView;
  /** The questions are asked above the plan (`PlanQuestionsCard`): do not repeat them here. */
  hideQuestions?: boolean;
  /** The task, so a plan item that is already in its package opens its diff and any other existing component the explorer (UX map C16). */
  snapshot?: TaskSnapshot | null;
  onOpenItem?: (taskId: string, workItemId: string) => void;
  onOpenFull?: () => void;
}) {
  const [all, setAll] = useState(false);
  const chips = useMemo(() => buildChipIndex(snapshot ? { [snapshot.task_id]: snapshot } : {}).byKey, [snapshot]);
  const items = plan.items ?? [];
  const shown = all ? items : items.slice(0, ITEMS_SHOWN);
  const hidden = items.length - shown.length;
  const notes: string[] = [];
  if (plan.assumptions?.length) notes.push(`${plan.assumptions.length} ${plan.assumptions.length === 1 ? "assumption" : "assumptions"}`);
  // open questions are SHOWN below, not counted: a reader approving a plan must see what it left open (dev 2026-11-02: "1 open
  // question" sat on the card with the question itself one click away in the workspace)
  const questions = hideQuestions ? [] : (plan.open_questions ?? []);
  const questionsShown = questions.slice(0, QUESTIONS_SHOWN);
  if (questions.length > questionsShown.length) notes.push(`${questions.length - questionsShown.length} more open ${questions.length - questionsShown.length === 1 ? "question" : "questions"}`);
  if (plan.out_of_scope?.length) notes.push(`${plan.out_of_scope.length} not included`);
  if (plan.org_findings_total) notes.push(`${plan.org_findings_total} org ${plan.org_findings_total === 1 ? "finding" : "findings"}`);
  return (
    <div className="flex flex-col gap-1.5" data-lane-v2-plan-summary={plan.plan_revision_id}>
      {plan.goal && <p className="text-[13px] text-foreground">{plan.goal}</p>}
      {items.length > 0 && (
        <ol className="flex flex-col gap-0.5">
          {shown.map((item, i) => {
            const op = OP_GLYPH[item.op] ?? { glyph: "·", className: "text-muted-foreground" };
            return (
              <li key={item.item_id} className="text-[12.5px]">
                <div className="flex items-baseline gap-2">
                  <span className="w-4 shrink-0 text-right tabular-nums text-muted-foreground">{i + 1}.</span>
                  <span aria-hidden className={cn("w-3 shrink-0 text-center font-semibold", op.className)}>{op.glyph}</span>
                  <span className="min-w-0 truncate">
                    <EntityChip target={chipTargetFor(chips, item.metadata_type, item.full_name, item.op !== "create")} onOpenDiff={onOpenItem} />
                  </span>
                  <span className="shrink-0 text-[11px] text-muted-foreground">{item.metadata_type}</span>
                  {item.irreversible && (
                    <span className="shrink-0 rounded border border-ma-err-line px-1 text-[11px] font-medium text-[var(--ma-state-err-text)]">
                      can't be undone
                    </span>
                  )}
                </div>
                <PlanItemDetail item={item} className="ml-6" />
              </li>
            );
          })}
        </ol>
      )}
      {questionsShown.length > 0 && (
        <ul className="flex w-fit max-w-full flex-col gap-1 rounded-md border border-ma-warn-line bg-ma-warn-soft px-2.5 py-1.5" data-lane-v2-open-questions="">
          {questionsShown.map((q, i) => (
            <li key={i} className="flex gap-1.5 text-[12.5px] leading-snug text-foreground">
              <span aria-hidden className="shrink-0 font-semibold text-[var(--ma-state-warn-text)]">?</span>
              <span className="min-w-0">
                <span className="sr-only">Open question: </span>
                {q}
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted-foreground">
        {hidden > 0 && (
          <button type="button" onClick={() => setAll(true)} className="font-medium hover:text-foreground">
            Show all {items.length} items
          </button>
        )}
        {notes.length > 0 && <span>{notes.join(" · ")}</span>}
        {onOpenFull && (
          <button type="button" onClick={onOpenFull} className="font-medium hover:text-foreground">
            View full plan
          </button>
        )}
      </div>
    </div>
  );
}

/** The package as a decision: what will be applied, in one line, with the way to review it. */
export function LaneV2PackageSummary({ pkg, onReview }: { pkg: PackageViewData; onReview?: () => void }) {
  const members = pkg.members?.length ?? 0;
  const excluded = pkg.excluded?.length ?? 0;
  const held = (pkg.members ?? []).filter((m) => isHeldBack(m as { validation_fact?: { reason?: string | null } })).length;
  const parts = [`${members} ${members === 1 ? "component" : "components"}`];
  if (held) parts.push(`${held} ${HELD_BACK_LABEL.toLowerCase()}`);
  if (excluded) parts.push(`${excluded} not included`);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]" data-lane-v2-package-summary={pkg.package_id}>
      <span className="text-foreground">{parts.join(" · ")}</span>
      {onReview && (
        <button type="button" onClick={onReview} className="text-[12px] font-medium text-muted-foreground hover:text-foreground">
          Review changes
        </button>
      )}
    </div>
  );
}
